import { motion } from 'framer-motion'
import { LucideIcon } from 'lucide-react'

interface PageHeroProps {
  icon?: LucideIcon
  title: string
  titleAccent?: string // Teil des Titels, der in der Highlight-Farbe erscheint
  description: string
  badge?: {
    icon: LucideIcon
    text: string
  }
  backgroundImage?: string // Optionales Hintergrundbild
}

export default function PageHero({ 
  icon: Icon, 
  title, 
  titleAccent,
  description, 
  badge,
  backgroundImage
}: PageHeroProps) {
  // Animation Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
  }

  const fadeInScale = {
    hidden: { opacity: 0, scale: 0.96 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  }

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.08,
      },
    },
  }

  return (
    <section className="relative h-[50vh] min-h-[350px] sm:min-h-[400px] max-h-[550px] w-full overflow-hidden flex items-center">
      {/* Hintergrundbild, falls angegeben */}
      {backgroundImage && (
        <div className="absolute inset-0 z-0">
          <img
            src={backgroundImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        </div>
      )}
      <div className="container mx-auto px-4 sm:px-6 md:px-12 lg:px-24 relative z-10 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Icon */}
          {Icon && (
            <motion.div variants={fadeInScale} className="mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-brand-800/10 border border-brand-800/20">
                <Icon className="w-7 h-7 text-brand-400" />
              </div>
            </motion.div>
          )}

          {/* Hauptüberschrift */}
          <motion.h1
            variants={fadeInUp}
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-4 md:mb-6 leading-tight tracking-tight"
          >
            {titleAccent ? (
              <>
                <span className="text-white">{title}</span>
                <br />
                <span className="bg-gradient-to-r from-brand-800 to-brand-700 bg-clip-text text-transparent">
                  {titleAccent}
                </span>
              </>
            ) : (
              <span className="text-white">{title}</span>
            )}
          </motion.h1>

          {/* Elegante Ornament-Linie */}
          <motion.div
            variants={fadeInUp}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              className="h-px w-16 md:w-20 bg-gradient-to-r from-transparent via-brand-800/40 to-brand-800"
            ></motion.div>
            <div className="w-1.5 h-1.5 bg-brand-800/50 rounded-full"></div>
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
              className="h-px w-16 md:w-20 bg-gradient-to-l from-transparent via-brand-800/40 to-brand-800"
            ></motion.div>
          </motion.div>

          {/* Beschreibung */}
          <motion.p
            variants={fadeInUp}
            className="font-sans text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto px-4"
          >
            {description}
          </motion.p>

          {/* Badge - Optional */}
          {badge && (
            <motion.div variants={fadeInUp} className="mt-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900/80 border border-brand-800/30 text-brand-400 text-xs tracking-wider uppercase font-semibold rounded-full">
                <badge.icon className="w-4 h-4" />
                <span>{badge.text}</span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  )
}
